import { inject } from '@angular/core';
import { signalStore, type, withState, withMethods, patchState, withHooks } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap, catchError, delay, retryWhen, take, timer, throwError, EMPTY, retry, Subscription, of, concatMap, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { setPropError, setPropLoaded, setPropLoading, withReqState, setPropInit } from '@application/data-access';
import {
  addEntity,
  entityConfig,
  setEntities,
  updateEntity,
  removeEntity,
  withEntities
} from '@ngrx/signals/entities';
import {
  AccessEvalResult,
  EdgeItem,
  EdgeStatus,
  HubConnectionStatus,
  AccessPoint,
  CreateAccessPointRequest,
  UpdateAccessPointRequest,
  AccessPointResponse,
  ODataAccessPointResponse,
  DirectionControlEnum,
  PassagePhysicalDirectionEnum,
  AccessPointBackendTypeEnum,
  convertEnumToNumber,
  GatePassage,
  ELogicalDirectionEnum,
  EvaluateByPersonIdRequest,
  AccessMethodEnum,
  AccessIntentEnum,
} from '../entities/access-edges.interface';
import { AccessPointEdgeService } from '../infra/access-point-edge.service';
import {
  AccessPointEdgeAccessEvalResultEvent,
  AccessPointEdgeGatePassageEvent,
  AccessPointEdgeNetworkInfoEvent,
  AccessPointEdgeHubService
} from '../infra/access-point-edge-hub.service';
import { tapResponse } from '@ngrx/operators';

export type TAccessPointEdgeStore = {
  publicIp: string | null;
  hubConnectionStatus: HubConnectionStatus;
  lastReleaseByEdgeId: Record<string, GatePassage>;
  lastAccessEvalResultByEdgeId: Record<string, AccessEvalResult>;
  isWizardFlowActive: boolean;
  lastDirection: ELogicalDirectionEnum | null;
  lastEvaluateByPersonIdAccessIntent: AccessIntentEnum | null;
  /** LogicalDirection from EvaluateByPersonId response (1 = Entry, 2 = Exit). */
  lastEvaluateByPersonIdLogicalDirection: number | null;
};

export const initialAccessPointEdgeStore: TAccessPointEdgeStore = {
  publicIp: null,
  hubConnectionStatus: 'disconnected',
  lastReleaseByEdgeId: {},
  lastAccessEvalResultByEdgeId: {},
  isWizardFlowActive: false,
  lastDirection: null,
  lastEvaluateByPersonIdAccessIntent: null,
  lastEvaluateByPersonIdLogicalDirection: null,
};

const configEdge = entityConfig({
  entity: type<EdgeItem>(),
  collection: 'edges',
  selectId: (edge: EdgeItem) => edge.EdgeId
});

const configAccessPoint = entityConfig({
  entity: type<AccessPoint>(),
  collection: 'accessPoints',
  selectId: (accessPoint: AccessPoint) => accessPoint.AccessPointId
});

export const AccessPointEdgeStore = signalStore(
  { providedIn: 'root' },
  withState<TAccessPointEdgeStore>(initialAccessPointEdgeStore),
  withEntities(configEdge),
  withEntities(configAccessPoint),
  withMethods(
    (
      store,
      edgesService = inject(AccessPointEdgeService),
      errorStore = inject(ErrorStore),
      hubService = inject(AccessPointEdgeHubService),
    ) => ({
      getPublicIp: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('getPublicIp'))),
        switchMap(() => edgesService.getPublicIp().pipe(
          retryWhen((errors) =>
            errors.pipe(
              delay(1000),
              take(3)
            )
          ),
          catchError(() => edgesService.getPublicIpFallback().pipe(
            retryWhen((errors) =>
              errors.pipe(
                delay(1000),
                take(2)
              )
            )
          )),
          tapResponse({
            next: (response) => {
              const ip = response?.ip || '';
              patchState(store, { publicIp: ip }, setPropLoaded('getPublicIp'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('getPublicIp', error));
            }
          })
        ))
      )),

      loadInitialDevices: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('loadInitialDevices'))),
        switchMap(() => edgesService.loadInitialDevices().pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: (response) => {
              const devices = Array.isArray(response)
                ? response
                : Array.isArray((response as any)?.items)
                ? (response as any).items
                : [];

              if (devices.length === 0) {
                patchState(store, setPropLoaded('loadInitialDevices'));
                return;
              }

              const edges: EdgeItem[] = devices
                .map((device) => {
                  const edgeId = device.edgeId || device.EdgeId;
                  const apiKeyKind = device.apiKeyKind ?? device.ApiKeyKind;
                  if (!edgeId) return null;
                  
                  return {
                    EdgeId: edgeId,
                    Status: 'online' as EdgeStatus,
                    LastTs: new Date().toISOString(),
                    ApiKeyKind: apiKeyKind,
                  };
                })
                .filter((edge): edge is EdgeItem => edge !== null);

              patchState(store, 
                setEntities(edges, configEdge),
                setPropLoaded('loadInitialDevices')
              );
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadInitialDevices', error));
            }
          })
        ))
      )),

      requestNetworkInfo: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('requestNetworkInfo'))),
        switchMap((publicIp) => {
          if (!publicIp) {
            patchState(store, setPropInit('requestNetworkInfo'));
            return EMPTY;
          }
          
          return edgesService.requestNetworkInfo(publicIp).pipe(
            retry({
              count: 3,
              delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
            }),
            tapResponse({
              next: () => {
                patchState(store, setPropLoaded('requestNetworkInfo'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('requestNetworkInfo', error));
              }
            })
          );
        })
      )),

      bindEdge: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('bindEdge'))),
        switchMap((edgeId) => edgesService.bindEdge(edgeId).pipe(
          retry({
            count: 2,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: () => {
              patchState(store, setPropLoaded('bindEdge'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('bindEdge', error));
            }
          })
        ))
      )),

      getEquipmentByEdgeId: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('getEquipmentByEdgeId'))),
        switchMap((edgeId) => edgesService.getEquipmentByEdgeId(edgeId).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: () => {
              patchState(store, setPropLoaded('getEquipmentByEdgeId'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('getEquipmentByEdgeId', error));
            }
          })
        ))
      )),

      loadAccessPoints: rxMethod<void>(pipe(
        tap(() => patchState(store, setPropLoading('loadAccessPoints'))),
        switchMap(() => edgesService.getAccessPoints().pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: (response) => {

              let responseArray: any[] = [];
              
              if (response && typeof response === 'object' && 'value' in response && Array.isArray((response as any).value)) {
                responseArray = (response as ODataAccessPointResponse).value;
              } else if (Array.isArray(response)) {
                responseArray = response;
              } else if (response && typeof response === 'object' && 'AccessPointId' in response) {
                responseArray = [response];
              }

              const edges = Object.values(store.edgesEntities());
              const edgesMap = new Map(edges.map(edge => [edge.EdgeId, edge]));

              const accessPoints: AccessPoint[] = responseArray.map((ap: AccessPointResponse) => {
                const edgeConfigurationDeployment = ap.EdgeConfigurationDeployment;
                const edgeId = edgeConfigurationDeployment?.EdgeId || '';
                
                let status: EdgeStatus = 'unknown';
                if (edgeId) {
                  const edge = edgesMap.get(edgeId);
                  if (edge) {
                    status = edge.Status;
                  } else {
                    const isActiveDeployment = edgeConfigurationDeployment?.IsActive ?? false;
                    if (isActiveDeployment) {
                      status = 'unknown';
                    } else {
                      status = 'offline';
                    }
                  }
                }

                const lastSeenUtc = edgeConfigurationDeployment?.LastSeenUtc;
           
                const backendTypeEnum = convertEnumToNumber(AccessPointBackendTypeEnum, ap.BackendTypeEnum) ?? 0;
                const entryMode = convertEnumToNumber(DirectionControlEnum, ap.EntryMode) ?? DirectionControlEnum.Free;
                const exitMode = convertEnumToNumber(DirectionControlEnum, ap.ExitMode) ?? DirectionControlEnum.Free;
                const entryDirection = convertEnumToNumber(PassagePhysicalDirectionEnum, ap.EntryDirection) ?? PassagePhysicalDirectionEnum.Clockwise;
                return {
                  AccessPointId: ap.AccessPointId,
                  EquipmentId: ap.EquipmentId,
                  Name: ap.Name,
                  IsActive: ap.IsActive,
                  BackendTypeEnum: backendTypeEnum,
                  CameraDeviceId: ap.CameraDeviceId,
                  CameraDevice: ap.CameraDevice,
                  EntryMode: entryMode,
                  ExitMode: exitMode,
                  EntryDirection: entryDirection,
                  IsSplitScreenMode: ap.IsSplitScreenMode,
                  ShowAdvertisement: ap.ShowAdvertisement,
                  ReleaseDurationSeconds: ap.ReleaseDurationSeconds ?? 1,
                  GateNumber: ap.GateNumber,
                  EdgeId: edgeId,
                  Status: status,
                  LastTs: lastSeenUtc || new Date().toISOString(),
                };
              }).filter(ap => ap.AccessPointId && ap.AccessPointId.trim() !== '');

              patchState(store,
                setEntities(accessPoints, configAccessPoint),
                setPropLoaded('loadAccessPoints')
              );
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadAccessPoints', error));
            }
          })
        ))
      )),

      createAccessPointInitial: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('createAccessPointInitial'))),
        switchMap((edgeId) => {
          const request: CreateAccessPointRequest = {
            EquipmentId: null,
            Name: 'Novo ponto de acesso',
            IsActive: true,
            BackendTypeEnum: 0,
            CameraDeviceId: null,
            CameraDevice: null,
            EntryMode: DirectionControlEnum.Free,
            ExitMode: DirectionControlEnum.Free,
            EntryDirection: PassagePhysicalDirectionEnum.Clockwise,
            ReleaseDurationSeconds: 1,
            IsSplitScreenMode: true,
            ShowAdvertisement: true,
          };

          return edgesService.createAccessPoint(request).pipe(
            retry({
              count: 3,
              delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
            }),
            tapResponse({
              next: (response) => {
                const backendTypeEnum = convertEnumToNumber(AccessPointBackendTypeEnum, response.BackendTypeEnum) ?? 0;
                const entryMode = convertEnumToNumber(DirectionControlEnum, response.EntryMode) ?? DirectionControlEnum.Free;
                const exitMode = convertEnumToNumber(DirectionControlEnum, response.ExitMode) ?? DirectionControlEnum.Free;
                const entryDirection = convertEnumToNumber(PassagePhysicalDirectionEnum, response.EntryDirection) ?? PassagePhysicalDirectionEnum.Clockwise;

                const accessPoint: AccessPoint = {
                  AccessPointId: response.AccessPointId,
                  EquipmentId: response.EquipmentId,
                  Name: response.Name,
                  IsActive: response.IsActive,
                  BackendTypeEnum: backendTypeEnum,
                  CameraDeviceId: response.CameraDeviceId,
                  CameraDevice: response.CameraDevice,
                  EntryMode: entryMode,
                  ExitMode: exitMode,
                  EntryDirection: entryDirection,
                  IsSplitScreenMode: response.IsSplitScreenMode,
                  ShowAdvertisement: response.ShowAdvertisement,
                  ReleaseDurationSeconds: response.ReleaseDurationSeconds ?? 1,
                  GateNumber: response.GateNumber,
                  EdgeId: edgeId,
                  Status: 'unknown' as EdgeStatus,
                  LastTs: new Date().toISOString(),
                };

                patchState(store,
                  addEntity(accessPoint, configAccessPoint),
                  setPropLoaded('createAccessPointInitial')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('createAccessPointInitial', error));
              }
            })
          );
        })
      )),

      updateAccessPoint: rxMethod<{ data: UpdateAccessPointRequest }>(pipe(
        tap(() => patchState(store, setPropLoading('updateAccessPoint'))),
        switchMap(({ data }) => edgesService.updateAccessPoint(data).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: (response) => {
              const existing = store.accessPointsEntityMap()[data.AccessPointId];
              if (existing) {
                const backendTypeEnum = response.BackendTypeEnum !== undefined 
                  ? (convertEnumToNumber(AccessPointBackendTypeEnum, response.BackendTypeEnum) ?? existing.BackendTypeEnum)
                  : existing.BackendTypeEnum;
                const entryMode = response.EntryMode !== undefined
                  ? (convertEnumToNumber(DirectionControlEnum, response.EntryMode) ?? existing.EntryMode)
                  : existing.EntryMode;
                const exitMode = response.ExitMode !== undefined
                  ? (convertEnumToNumber(DirectionControlEnum, response.ExitMode) ?? existing.ExitMode)
                  : existing.ExitMode;
                const entryDirection = response.EntryDirection !== undefined
                  ? (convertEnumToNumber(PassagePhysicalDirectionEnum, response.EntryDirection) ?? existing.EntryDirection)
                  : existing.EntryDirection;

                const updated: AccessPoint = {
                  ...existing,
                  AccessPointId: response.AccessPointId,
                  EquipmentId: response.EquipmentId ?? existing.EquipmentId,
                  Name: response.Name ?? existing.Name,
                  IsActive: response.IsActive ?? existing.IsActive,
                  BackendTypeEnum: backendTypeEnum,
                  CameraDeviceId: response.CameraDeviceId ?? existing.CameraDeviceId,
                  CameraDevice: response.CameraDevice ?? existing.CameraDevice,
                  EntryMode: entryMode,
                  ExitMode: exitMode,
                  EntryDirection: entryDirection,
                  IsSplitScreenMode: response.IsSplitScreenMode ?? existing.IsSplitScreenMode,
                  ShowAdvertisement: response.ShowAdvertisement ?? existing.ShowAdvertisement,
                  ReleaseDurationSeconds: response.ReleaseDurationSeconds ?? existing.ReleaseDurationSeconds,
                  GateNumber: response.GateNumber ?? existing.GateNumber,
                };

                patchState(store,
                  updateEntity({ id: data.AccessPointId, changes: updated }, configAccessPoint),
                  setPropLoaded('updateAccessPoint')
                );
              } else {  
                patchState(store, setPropLoaded('updateAccessPoint'));
              }
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('updateAccessPoint', error));
            }
          })
        ))
      )),

      configureDevice: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('configureDevice'))),
        switchMap((edgeId) => {
          return edgesService.bindEdge(edgeId).pipe(
            retry({
              count: 2,
              delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
            }),
            switchMap(() => of(edgeId)),
            catchError((error: HttpErrorResponse) => {
              const isAlreadyBound = error.status === 409 || 
                                    (error.status === 400 && error.error?.toString().toLowerCase().includes('already'));
              
              if (isAlreadyBound) {
                return of(edgeId);
              } else {
                errorStore.setHttpError(error);
                patchState(store, setPropError('bindEdge', error));
                patchState(store, setPropError('configureDevice', error));
                return EMPTY;
              }
            })
          );
        }),
        switchMap((edgeId) => edgesService.getByEdgeId(edgeId).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          concatMap((equipmentResponse) => {

            if (!equipmentResponse) {
              return throwError(() => new Error('Equipment not found'));
            }

            const createData: CreateAccessPointRequest = {
              EquipmentId: equipmentResponse.EquipmentId,
              Name: equipmentResponse?.Name || 'Novo ponto de acesso',
              IsActive: true,
              BackendTypeEnum: 0,
              CameraDeviceId: null,
              CameraDevice: null,
              EntryMode: DirectionControlEnum.Free,
              ExitMode: DirectionControlEnum.Free,
              EntryDirection: PassagePhysicalDirectionEnum.Clockwise,
              ReleaseDurationSeconds: 1,
              IsSplitScreenMode: true,
              ShowAdvertisement: true,
            };

            return edgesService.createAccessPoint(createData).pipe(
              retry({
                count: 3,
                delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
              }),
              tapResponse({
                next: (response) => {
                  const backendTypeEnum = convertEnumToNumber(AccessPointBackendTypeEnum, response.BackendTypeEnum) ?? 0;
                  const entryMode = convertEnumToNumber(DirectionControlEnum, response.EntryMode) ?? DirectionControlEnum.Free;
                  const exitMode = convertEnumToNumber(DirectionControlEnum, response.ExitMode) ?? DirectionControlEnum.Free;
                  const entryDirection = convertEnumToNumber(PassagePhysicalDirectionEnum, response.EntryDirection) ?? PassagePhysicalDirectionEnum.Clockwise;

                  const accessPoint: AccessPoint = {
                    AccessPointId: response.AccessPointId,
                    EquipmentId: response.EquipmentId,
                    Name: response.Name,
                    IsActive: response.IsActive,
                    BackendTypeEnum: backendTypeEnum,
                    CameraDeviceId: response.CameraDeviceId,
                    CameraDevice: response.CameraDevice,
                    EntryMode: entryMode,
                    ExitMode: exitMode,
                    EntryDirection: entryDirection,
                    IsSplitScreenMode: response.IsSplitScreenMode,
                    ShowAdvertisement: response.ShowAdvertisement,
                    ReleaseDurationSeconds: response.ReleaseDurationSeconds ?? 1,
                    GateNumber: response.GateNumber,
                    EdgeId: edgeId,
                    Status: 'unknown' as EdgeStatus,
                    LastTs: new Date().toISOString(),
                  };

                  const existing = store.accessPointsEntities()[accessPoint.AccessPointId];
                  if (existing) {
                    patchState(store,
                      updateEntity({ id: accessPoint.AccessPointId, changes: accessPoint }, configAccessPoint),
                      setPropLoaded('configureDevice')
                    );
                  } else {
                    patchState(store,
                      addEntity(accessPoint, configAccessPoint),
                      setPropLoaded('configureDevice')
                    );
                  }
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createAccessPointInitial', error));
                  patchState(store, setPropError('configureDevice', error));
                }
              })
            );
          }),
          catchError((error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, setPropError('getEquipmentByEdgeId', error));
            patchState(store, setPropError('configureDevice', error));
            return EMPTY;
          })
        )),
        catchError((error: HttpErrorResponse) => {
          errorStore.setHttpError(error);
          patchState(store, setPropError('configureDevice', error));
          return EMPTY;
        })
      )),

      updateAccessPointStatus: (accessPointId: string, status: EdgeStatus): void => {
        const existing = store.accessPointsEntities()[accessPointId];
        if (existing) {
          patchState(store, updateEntity({
            id: accessPointId,
            changes: {
              Status: status,
              LastTs: new Date().toISOString(),
            }
          }, configAccessPoint));
        }
      },

      upsertAccessPoint: (update: Partial<AccessPoint> & { AccessPointId: string }): void => {
        const existing = store.accessPointsEntities()[update.AccessPointId];
        const merged: AccessPoint = {
          ...existing,
          ...update,
          AccessPointId: update.AccessPointId,
          Status: update.Status ?? existing?.Status ?? 'unknown',
          LastTs: update.LastTs ?? new Date().toISOString(),
        };

        if (existing) {
          patchState(store, updateEntity({ id: update.AccessPointId, changes: merged }, configAccessPoint));
        } else {
          patchState(store, addEntity(merged, configAccessPoint));
        }
      },

      handleNewEdgeId: (edgeId: string): void => {
        const accessPoints = Object.values(store.accessPointsEntities());
        const existingAccessPoint = accessPoints.find(ap => ap.EdgeId === edgeId);

        if (!existingAccessPoint) {
          (store as any).configureDevice(edgeId);
        }
      },

      upsertEdge: (update: Partial<EdgeItem>): void => {
        const existing = store.edgesEntityMap()[update.EdgeId];
        if (existing) {
          const updated: EdgeItem = {
            ...existing,
            ...update,
            EdgeId: update.EdgeId!,
            Status: update.Status ?? existing.Status,
            LastTs: update.LastTs ?? (update.Status !== undefined || update.ApiKeyKind !== undefined || update.Net !== undefined 
              ? new Date().toISOString() 
              : existing.LastTs),
          };
          patchState(store, updateEntity({ id: update.EdgeId, changes: updated }, configEdge));
        } else {
          const newEdge: EdgeItem = {
            EdgeId: update.EdgeId!,
            Status: update.Status ?? 'unknown',
            LastTs: update.LastTs ?? new Date().toISOString(),
            ApiKeyKind: update.ApiKeyKind,
            Net: update.Net,
            LastGatePassage: update.LastGatePassage,
          };
          
          patchState(store, addEntity(newEdge, configEdge));
        }
      },

      setHubConnectionStatus: (status: HubConnectionStatus): void => {
        patchState(store, { hubConnectionStatus: status });
      },

      setWizardFlowActive: (isActive: boolean): void => {
        patchState(store, { isWizardFlowActive: isActive });
      },

      startHub: async (): Promise<void> => {
        await hubService.start();
      },

      stopHub: async (): Promise<void> => {
        await hubService.stop();
      },

      resetReqState: () => {
        patchState(store, setPropInit(
          'getPublicIp',
          'loadInitialDevices',
          'requestNetworkInfo',
          'bindEdge',
          'getEquipmentByEdgeId',
          'loadAccessPoints',
          'createAccessPointInitial',
          'updateAccessPoint',
          'configureDevice',
          'releaseEdge',
          'evaluateByPersonId',
          'unbindEdge',
          'deleteAccessPoint'
        ));
      },

      resetEvaluateByPersonIdState: () => {
        patchState(store, setPropInit('evaluateByPersonId'), { lastEvaluateByPersonIdAccessIntent: null, lastEvaluateByPersonIdLogicalDirection: null });
      },

      isStale: (lastTs: string): boolean => {
        const lastUpdate = new Date(lastTs);
        const timeDiff = Date.now() - lastUpdate.getTime();
        return timeDiff > 45000;
      },

      releaseEdge: rxMethod<{ edgeId: string; direction: ELogicalDirectionEnum }>(pipe(
        tap(({ direction }) => patchState(store, setPropLoading('releaseEdge'), { lastDirection: direction })),
        switchMap(({ edgeId, direction }) =>
          edgesService.releaseEdge(edgeId, direction).pipe(
            tapResponse({
              next: () => {
                const current = store.lastReleaseByEdgeId ?? {};
                const updated: Record<string, { edgeId: string; direction: 'Entry' | 'Exit'; ts: string }> = {
                  ...current,
                  [edgeId]: {
                    edgeId,
                    direction,
                    ts: new Date().toISOString(),
                  } as any,
                } as any;

                patchState(store,
                  { lastReleaseByEdgeId: updated as any },
                  setPropLoaded('releaseEdge')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('releaseEdge', error));
              }
            })
          )
        )
      )),

      evaluateByPersonId: rxMethod<{ edgeId: string; personId: string; accessIntent: AccessIntentEnum }>(pipe(
        tap(({ accessIntent }) => patchState(store, setPropLoading('evaluateByPersonId'), { lastEvaluateByPersonIdAccessIntent: accessIntent })),
        switchMap(({ edgeId, personId, accessIntent }) => {
          const request: EvaluateByPersonIdRequest = {
            Identifier: personId,
            AccessMethod: AccessMethodEnum.PersonId,
            AccessIntent: accessIntent,
          };

          return edgesService.evaluateByPersonId(edgeId, request).pipe(
            tapResponse({
              next: (response: { Response?: { LogicalDirection?: number } }) => {
                const logicalDirection = response?.Response?.LogicalDirection ?? null;
                patchState(store, setPropLoaded('evaluateByPersonId'), { lastEvaluateByPersonIdLogicalDirection: logicalDirection });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, setPropError('evaluateByPersonId', error));
              }
            })
          );
        })
      )),

      unbindEdge: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('unbindEdge'))),
        switchMap((edgeId) => edgesService.unbindEdge(edgeId).pipe(
          retry({
            count: 2,
            delay: (error, retryCount) => timer(Math.min(1000 * Math.pow(2, retryCount - 1), 10000)),
          }),
          tapResponse({
            next: () => {
              patchState(store, setPropLoaded('unbindEdge'));
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('unbindEdge', error));
            }
          })
        ))
      )),

      deleteAccessPoint: rxMethod<{ accessPointId: string; edgeId?: string }>(pipe(
        tap(() => patchState(store, setPropLoading('deleteAccessPoint'))),
        switchMap(({ accessPointId, edgeId }) => {
          const trimmedEdgeId = edgeId?.trim();
          const unbind$ = trimmedEdgeId ? edgesService.unbindEdge(trimmedEdgeId) : of('');

          return unbind$.pipe(
            switchMap(() => edgesService.deleteAccessPoint(accessPointId)),
            tapResponse({
              next: () => {
                patchState(store,
                  removeEntity(accessPointId, configAccessPoint),
                  setPropLoaded('deleteAccessPoint')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('deleteAccessPoint', error));
              }
            })
          );
        })
      )),
    })
  ),
  withReqState(
    'getPublicIp',
    'loadInitialDevices',
    'requestNetworkInfo',
    'bindEdge',
    'getEquipmentByEdgeId',
    'loadAccessPoints',
    'createAccessPointInitial',
    'updateAccessPoint',
    'configureDevice',
    'releaseEdge',
    'evaluateByPersonId',
    'unbindEdge',
    'deleteAccessPoint'
  ),
  withHooks((store) => {
    const hubService = inject(AccessPointEdgeHubService);
    const subscriptions: Subscription[] = [];
    let staleCheckInterval: ReturnType<typeof setInterval> | null = null;
    const addSubscription = (subscription: Subscription) => subscriptions.push(subscription);

    return {
      onInit: () => {
        addSubscription(hubService.hubStatus$.subscribe((status) => store.setHubConnectionStatus(status)));

        addSubscription(hubService.edgeOnline$.subscribe(({ edgeId, apiKeyKind }) => {
          store.upsertEdge({
            EdgeId: edgeId,
            Status: 'online',
            ApiKeyKind: apiKeyKind,
            LastTs: new Date().toISOString(),
          });
    
        }));

        addSubscription(hubService.edgeOffline$.subscribe(({ edgeId }) => {
          store.upsertEdge({
            EdgeId: edgeId,
            Status: 'offline',
            LastTs: new Date().toISOString(),
          });
        }));

        addSubscription(hubService.heartbeat$.subscribe(({ edgeId }) => {
          store.upsertEdge({
            EdgeId: edgeId,
            Status: 'online',
            LastTs: new Date().toISOString(),
          });
        }));

        addSubscription(hubService.networkInfo$.subscribe((event: AccessPointEdgeNetworkInfoEvent) => {
          store.upsertEdge({
            EdgeId: event.edgeId,
            Status: 'online',
            LastTs: new Date().toISOString(),
          });
        }));

        addSubscription(hubService.gatePassage$.subscribe((event: AccessPointEdgeGatePassageEvent) => {
          store.upsertEdge({
            EdgeId: event.edgeId,
            LastGatePassage: event.lastGatePassage,
            Status: 'online',
            LastTs: new Date().toISOString(),
          });
        }));

        addSubscription(hubService.accessEvalResult$.subscribe((event: AccessPointEdgeAccessEvalResultEvent) => {
          const current = store.lastAccessEvalResultByEdgeId();
          patchState(store, {
            lastAccessEvalResultByEdgeId: {
              ...current,
              [event.edgeId]: event.accessEvalResult,
            },
          });
          store.upsertEdge({
            EdgeId: event.edgeId,
            Status: 'online',
            LastTs: new Date().toISOString(),
          });
        }));
      },
      onDestroy: () => {
        subscriptions.forEach((subscription) => subscription.unsubscribe());
        subscriptions.length = 0;
      }
    };
  })
);

